import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Card from "components/Card/Card";
import Button from "components/Button/Regular";
import { useActiveOrg } from "context/ActiveOrgContext";
import vgToast from "utils/toast";
import {
  ARRANGEMENT_STATUS_LABEL,
  Arrangement,
  newArrangement,
  readArrangements,
  saveArrangement,
} from "services/arrangements.service";

/** A funeral home's arrangements, newest first, and starting a new one from a first call. */
const ArrangementsList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [arrangements, setArrangements] = React.useState<Arrangement[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    if (orgId <= 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const { data, error: readError } = await readArrangements(token, orgId);
      if (cancelled) return;
      setLoading(false);
      if (readError) setError(readError.message);
      else setArrangements(data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: saveError } = await saveArrangement(token, { ...newArrangement(orgId), deceasedFullName: name });
      if (saveError || !data) {
        vgToast.error(saveError?.message ?? "Could not start the arrangement.");
        return;
      }
      navigate(`/app/arrangements/${data.arrangementId}`);
    } finally {
      setCreating(false);
    }
  };

  if (orgId <= 0) {
    return (
      <Page title="Arrangements">
        <p className="text-sm text-slate-500">Choose an active provider from the header.</p>
      </Page>
    );
  }

  return (
    <Page title="Arrangements" subTitle="Each funeral you are arranging, from the first call to the day.">
      <div className="flex flex-col gap-5">
        <Card title="Start an arrangement" subTitle="You can add everything else afterwards.">
          <form onSubmit={start} className="p-6 flex flex-col gap-4 sm:flex-row sm:items-end">
            <label className="flex grow flex-col gap-1.5 text-sm text-slate-600">
              Name of the person who has died
              <input
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <Button color="Primary" type="submit" disabled={creating}>
              <span>{creating ? "Starting…" : "Start"}</span>
            </Button>
          </form>
        </Card>

        <Card title="Arrangements">
          <div className="p-6">
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
            {!loading && !error && arrangements.length === 0 && (
              <p className="text-sm text-slate-500">No arrangements yet.</p>
            )}
            {arrangements.length > 0 && (
              <ul className="divide-y divide-slate-100">
                {arrangements.map((a) => (
                  <li key={a.arrangementId}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50"
                      onClick={() => navigate(`/app/arrangements/${a.arrangementId}`)}
                    >
                      <span>
                        <span className="block text-sm font-medium text-slate-800">{a.deceasedFullName}</span>
                        <span className="block text-xs text-slate-500">
                          {a.funeralDate ? `Funeral ${a.funeralDate}` : "No funeral date yet"}
                        </span>
                      </span>
                      <span className="text-xs font-medium text-slate-600">{ARRANGEMENT_STATUS_LABEL[a.status]}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
};

export default ArrangementsList;
